import { useCallback } from 'react';
import { get, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { TableRowPositionChangeHandler } from './types';
import {
  updateSequenceNumbers,
  useMoveFormTableRow,
} from './useMoveFormTableRow';
import { UUIDType } from '../../../utils';

const sequenceNumberFieldName = 'childSequenceNumber' as const;

export const useChangeTableRowPosition = (
  tableName: string,
  form: {
    getValues: UseFormGetValues<any>;
    setValue: UseFormSetValue<any>;
  },
  onParentChange?: (
    rowKey: UUIDType,
    value: UUIDType | null,
    levelNumber: number
  ) => void
): TableRowPositionChangeHandler => {
  const { getValues, setValue } = form;
  const move = useMoveFormTableRow(form, sequenceNumberFieldName);

  return useCallback(
    (
      fromPathIndexes,
      toPathIndexes,
      rowKey,
      fromParentKey,
      toParentKey,
      toDepthIndex
    ) => {
      const formData = getValues();

      const fromPath = getFieldPath(fromPathIndexes, tableName);
      const toPath = getFieldPath(toPathIndexes, tableName);

      const fromParentPath = fromPath.slice(0, -1).join('.');
      const toParentPath = toPath.slice(0, -1).join('.');

      const fromIndex = fromPath[fromPath.length - 1] as number;
      const toIndex = toPath[toPath.length - 1] as number;

      if (fromParentPath === toParentPath) {
        move(toParentPath, fromIndex, toIndex);
      } else {
        const fromArray = get(formData, fromParentPath);
        const toArray = get(formData, toParentPath, []); // Default to empty array in case target parent has no children
        const movable = get(formData, fromPath.join('.'));

        const inject = () => {
          setValue(
            toParentPath,
            [
              ...toArray.slice(0, toIndex),
              ...updateSequenceNumbers(
                toIndex + 1,
                [movable, ...toArray.slice(toIndex)],
                sequenceNumberFieldName
              ),
            ],
            { shouldDirty: false }
          );
        };

        const remove = () => {
          setValue(
            fromParentPath,
            [
              ...fromArray.slice(0, fromIndex),
              ...updateSequenceNumbers(
                fromIndex + 1,
                [...fromArray.slice(fromIndex + 1)],
                sequenceNumberFieldName
              ),
            ],
            { shouldDirty: false }
          );
        };

        if (isTargetingOtherBranch(fromPath, toPath)) {
          /**
           * Move to under a (great (great (...))) uncle or cousin: order or remove/inject operations is not important
           */
          inject();
          remove();
        } else if (fromPath.length < toPath.length) {
          /**
           * Descend (become a descendant of one its siblings): inject operation must happen before remove operation
           */
          inject();
          remove();
        } else if (fromPath.length > toPath.length) {
          /**
           * Ascend (become a sibling of one its ancestors): remove operation must happen before inject operation
           */
          remove();
          inject();
        } else {
          throw new Error(
            `Unhandled move operation from "${fromPath?.join(
              '.'
            )}" to "${toPath?.join('.')}"`
          );
        }
      }

      // Trigger re-render
      setValue(tableName, getValues(tableName), { shouldDirty: true });

      onParentChange?.(rowKey, toParentKey, toDepthIndex + 1);
    },
    [tableName, getValues, setValue, move, onParentChange]
  );
};

const getFieldPath = (pathIndexes: number[], tableName: string) =>
  pathIndexes.reduce<(string | number)[]>(
    (path, pathIndex, index) =>
      path.concat(index ? 'children' : tableName, pathIndex),
    []
  );

export const isTargetingOtherBranch = (
  fromPath: (string | number)[],
  toPath: (string | number)[]
) => {
  if (fromPath.length < 4 || toPath.length < 4) {
    return false;
  }
  const nexusIndex = Math.min(fromPath.length, toPath.length) - 3;
  return fromPath[nexusIndex] !== toPath[nexusIndex];
};
